import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../api/client';
import { useActiveSessions, useNearbyLots } from '../api/hooks';
import type { SessionView } from '../api/types';
import { BrandBar, InstallHint, OfflineBanner } from '../components/chrome';
import { PromoBanner } from '../components/promo-banner';
import { Card, ErrorState, Loading, Muted, Note, Screen, SecondaryButton, Title } from '../components/ui';
import { countdown, usd } from '../lib/format';
import { lotClockLabel, resolveLotTimeLanguage } from '../lib/lot-time';
import { useCountdown } from '../lib/use-countdown';

type Coords = { latitude: number; longitude: number };

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [coords, setCoords] = useState<Coords | undefined>();
  const [locationState, setLocationState] = useState<'asking' | 'granted' | 'denied'>('asking');
  const [code, setCode] = useState('');

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationState('denied');
      return;
    }
    setLocationState('asking');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        setLocationState('granted');
      },
      // Location only sorts the list and must never pick a lot for the driver;
      // refusing it leaves the flow completely usable (FR-U-11 / 17).
      () => setLocationState('denied'),
      { timeout: 8000, maximumAge: 300_000 },
    );
  }, []);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  const lots = useNearbyLots(coords);
  const sessions = useActiveSessions(tokens.signedIn);
  const active = sessions.data?.sessions ?? [];

  // The nearest lot's street confirms the sort is sane far better than the raw
  // permission state does.
  const count = lots.data?.lots.length ?? 0;
  const nearestStreet = lots.data?.lots[0]?.address.split(',')[0]?.trim();

  return (
    <>
      <BrandBar wide />
      <OfflineBanner />
      {/* Home is one of the three wide desktop pages (review §4.13). */}
      <Screen wide>
        {active.length > 0 ? (
          <div className="flex flex-col gap-3">
            {active.map((session) => (
              <ActiveSessionCard key={session.id} session={session} />
            ))}
          </div>
        ) : null}

        <Title>{t('home.title')}</Title>

        {/* Three same-rank ways to a lot — location, entrance QR, lot code —
            and all three must sit above the fold on a 390px phone, so each
            entry stays a single compact card. On the 800px desktop column the
            same three cards sit side by side; a 780px-wide single card would
            read as wasted space (review §4.13). Below lg the stack is
            untouched. */}
        <div className="flex flex-col gap-2 lg:grid lg:grid-cols-3">
          <Card>
            <p className="text-[15px] font-semibold">{t('home.useLocation')}</p>
            {locationState === 'denied' ? (
              <>
                <Muted>{t('home.locationOff', { count })}</Muted>
                {/* A failed or denied fetch must leave a live way back in, not
                    a silent dead state. */}
                <SecondaryButton label={t('home.locationRetry')} onClick={requestLocation} />
              </>
            ) : (
              <Muted>
                {coords ? t('home.located', { street: nearestStreet ?? '…', count }) : t('home.locating')}
              </Muted>
            )}
          </Card>

          {/* The entrance QR points AT this site, so there is nothing for the
              page itself to scan — the entry teaches the phone-camera gesture
              instead of faking a scanner button. */}
          <Card>
            <p className="text-[15px] font-semibold">{t('home.scanTitle')}</p>
            <Muted>{t('home.scanHint')}</Muted>
          </Card>

          <Card>
            <p className="text-[15px] font-semibold">{t('home.codeTitle')}</p>
            <Muted>{t('home.codeHint')}</Muted>
            {/* Stacked below 375px, one row from 375px up. On the row the input
                must shrink (min-w-0 beats the input's intrinsic min-width) and
                the button must not: its label stays on one line in EN and ZH. */}
            <div className="flex flex-col gap-2 xs:flex-row">
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                placeholder="4821"
                aria-label={t('home.codeLabel')}
                className="num min-h-[52px] w-full min-w-0 rounded-lg border-[1.5px] border-ink px-4 text-xl tracking-[4px] outline-none xs:flex-1"
              />
              <button
                type="button"
                disabled={code.length < 4}
                onClick={() => void navigate({ to: '/l/$code', params: { code } })}
                className="min-h-[52px] w-full whitespace-nowrap rounded-lg bg-ink px-5 text-[15px] font-semibold text-white disabled:opacity-40 xs:w-auto xs:shrink-0"
              >
                {t('home.codeGo')}
              </button>
            </div>
          </Card>
        </div>

        {lots.isPending ? (
          <Loading />
        ) : lots.isError ? (
          <ErrorState
            message={t('home.loadFailed')}
            hint={t('home.loadFailedHint')}
            onRetry={() => void lots.refetch()}
          />
        ) : (
          <div>
            {lots.data.lots.map((lot) => (
              <Link
                key={lot.id}
                to="/lot/$lotId"
                params={{ lotId: lot.id }}
                className="flex items-center gap-3 border-b border-hairline py-4"
              >
                <span className="flex flex-1 flex-col gap-1">
                  <span className="text-[17px] font-semibold">{lot.name}</span>
                  <span className="text-[13px] text-muted">
                    {[lot.nameZh, lot.distanceLabel].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="flex flex-col items-end gap-0.5">
                  <span className="num text-[17px] font-semibold">{usd(lot.fromCents)}</span>
                  <span className="text-[11px] text-muted">{t('home.fromPrice')}</span>
                </span>
              </Link>
            ))}
          </div>
        )}

        <Note>{t('home.locationNote')}</Note>

        {/* The credit promo must stay after the lot list: it advertises, the
            list parks the car. */}
        <PromoBanner lotId={lots.data?.lots[0]?.id} />
        <InstallHint />

        {!tokens.signedIn ? (
          <SecondaryButton
            label={t('home.signInPrompt')}
            onClick={() => void navigate({ to: '/login', search: { next: undefined } })}
          />
        ) : null}
      </Screen>
    </>
  );
}

/** An in-progress stay outranks everything else on this screen (FR-U-32). */
function ActiveSessionCard({ session }: { session: SessionView }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const remaining = useCountdown(session.expiresAt, session.remainingSeconds);
  // rounded-2xl: the active-session card is the one container allowed a
  // stronger radius than the 8px system (review §5).
  return (
    <div className="relative flex flex-col gap-3 rounded-2xl bg-ink p-5 text-white">
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-wider opacity-65">{t('home.inProgress')}</span>
        <span className="num text-xs opacity-65">{session.plate}</span>
      </div>
      <span className="num text-[52px] font-semibold leading-none tracking-[-2px]">
        {countdown(remaining)}
      </span>
      <span className="text-[13px] opacity-70">
        {t('home.expiresAt', {
          lot: session.lot.name,
          time: lotClockLabel(session.expiresAt, session.lot.timezone, resolveLotTimeLanguage(i18n.language)),
        })}
      </span>
      <div className="flex gap-2">
        {/* Blue is the extend tone (5f82c3f): black pays, blue extends. The
            button must stack above the stretched View link, hence z-10. */}
        <button
          type="button"
          onClick={() =>
            void navigate({ to: '/sessions/$sessionId/extend', params: { sessionId: session.id } })
          }
          className="relative z-10 min-h-[48px] flex-1 rounded-lg bg-accent text-[15px] font-semibold text-white"
        >
          {t('session.extend')}
        </button>
        {/* before:inset-0 stretches this link over the whole card, so the
            card tap and View stay one control — no nested interactives. */}
        <Link
          to="/sessions/$sessionId"
          params={{ sessionId: session.id }}
          className="flex min-h-[48px] flex-1 items-center justify-center rounded-lg border-[1.5px] border-white/30 text-[15px] font-semibold text-white before:absolute before:inset-0"
        >
          {t('home.viewSession')}
        </Link>
      </div>
    </div>
  );
}
